import { useTranslation } from '@silospot/i18n/react';
import type { Goal, GoalCategory, GoalInput, GoalTrigger } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { api, serverError } from '../../lib/api';
import { Badge, Btn, Card, CardHead, Segmented, Toggle } from './kit';

const TRIGGERS: GoalTrigger[] = ['url', 'form', 'phone', 'whatsapp', 'download', 'selector', 'api'];
const CATEGORIES: GoalCategory[] = [
  'purchase',
  'cart',
  'form',
  'newsletter',
  'phone',
  'download',
  'custom',
];

export function GoalsSection({ projectId }: { projectId: string }): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Goal | null>(null);
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['cld-goals', projectId],
    queryFn: () => api.listGoals(projectId),
    enabled: !!projectId,
  });
  const remove = useMutation({
    mutationFn: (goalId: number) => api.deleteGoal(projectId, goalId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cld-goals', projectId] }),
  });
  const goals = data?.goals ?? [];

  return (
    <Card className="lg:col-span-2">
      <CardHead
        title={t('goals.title')}
        right={
          <Btn
            icon={Plus}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            {t('goals.add')}
          </Btn>
        }
      />
      {goals.length === 0 ? (
        <p className="py-4 text-[13px] text-text-3">{t('goals.empty')}</p>
      ) : (
        <div className="flex flex-col">
          {goals.map((g) => (
            <div
              key={g.id}
              className="flex flex-wrap items-center gap-3 border-b border-[#f1f3f6] py-2.5 last:border-0"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: g.active ? '#16a34a' : '#cbd2dc' }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-text">{g.name}</div>
                <div className="truncate text-[11.5px] text-text-3">
                  {t(`goals.t${cap(g.triggerType)}`)}
                  {g.matcher ? ` · ${g.matcher}` : ''}
                </div>
              </div>
              <Badge tone="neutral">{t(`cat.${g.category}`)}</Badge>
              <button
                type="button"
                aria-label="edit"
                onClick={() => {
                  setEditing(g);
                  setOpen(true);
                }}
                className="grid h-8 w-8 place-items-center rounded-md text-text-2 hover:bg-bg"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="delete"
                onClick={() => remove.mutate(g.id)}
                className="grid h-8 w-8 place-items-center rounded-md text-fraud-fg hover:bg-fraud-bg"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <GoalModal projectId={projectId} goal={editing} onClose={() => setOpen(false)} />
      ) : null}
    </Card>
  );
}

function GoalModal({
  projectId,
  goal,
  onClose,
}: {
  projectId: string;
  goal: Goal | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [form, setForm] = useState<GoalInput>({
    name: goal?.name ?? '',
    category: goal?.category ?? 'purchase',
    triggerType: goal?.triggerType ?? 'url',
    matcher: goal?.matcher ?? '',
    matchMode: goal?.matchMode ?? 'contains',
    valueType: goal?.valueType ?? 'none',
    valueAmount: goal?.valueAmount ?? 0,
    valueParam: goal?.valueParam ?? '',
    currency: goal?.currency ?? 'EUR',
    active: goal?.active ?? true,
  });
  const set = <K extends keyof GoalInput>(k: K, v: GoalInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () =>
      goal ? api.updateGoal(projectId, goal.id, form) : api.createGoal(projectId, form),
    onSuccess: () => {
      toast.success(t('goals.saved'));
      void qc.invalidateQueries({ queryKey: ['cld-goals', projectId] });
      onClose();
    },
    onError: (e) => toast.error(serverError(e) ?? t('goals.saveError')),
  });

  const needsMatcher = ['url', 'form', 'selector', 'api'].includes(form.triggerType);
  const matcherLabel =
    form.triggerType === 'selector'
      ? t('goals.matcherSelector')
      : form.triggerType === 'api'
        ? t('goals.matcherApi')
        : form.triggerType === 'form'
          ? t('goals.matcherForm')
          : t('goals.matcherUrl');

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-5">
      <button
        type="button"
        aria-label={t('goals.cancel')}
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(15,21,32,0.42)] backdrop-blur-[3px]"
      />
      {/* biome-ignore lint/a11y/useSemanticElements: styled modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('goals.add')}
        className="relative max-h-[90vh] w-[480px] max-w-full overflow-y-auto rounded-2xl bg-surface p-6 shadow-[0_24px_60px_rgba(16,24,40,0.3)]"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[16px] font-bold text-text">{t('goals.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('goals.cancel')}
            className="grid h-8 w-8 place-items-center rounded-md text-text-2 hover:bg-bg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Field label={t('goals.name')}>
          <input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            className={inputCls}
          />
        </Field>

        <Field label={t('goals.trigger')}>
          <select
            value={form.triggerType}
            onChange={(e) => set('triggerType', e.target.value as GoalTrigger)}
            className={inputCls}
          >
            {TRIGGERS.map((tr) => (
              <option key={tr} value={tr}>
                {t(`goals.t${cap(tr)}`)}
              </option>
            ))}
          </select>
        </Field>

        {needsMatcher ? (
          <Field label={matcherLabel}>
            <input
              value={form.matcher}
              onChange={(e) => set('matcher', e.target.value)}
              className={inputCls}
              placeholder={
                form.triggerType === 'selector'
                  ? '.add-to-cart'
                  : form.triggerType === 'url'
                    ? '/merci'
                    : ''
              }
            />
            {form.triggerType === 'selector' ? (
              <p className="mt-1 text-[11px] text-text-3">{t('goals.selectorHint')}</p>
            ) : null}
            {form.triggerType === 'api' ? (
              <p className="mt-1 text-[11px] text-text-3">{t('goals.apiHint')}</p>
            ) : null}
            {form.triggerType === 'url' ? (
              <div className="mt-2">
                <Segmented
                  size="sm"
                  value={form.matchMode}
                  onChange={(v) => set('matchMode', v)}
                  options={[
                    { value: 'contains', label: t('goals.matchContains') },
                    { value: 'equals', label: t('goals.matchEquals') },
                    { value: 'regex', label: t('goals.matchRegex') },
                  ]}
                />
              </div>
            ) : null}
          </Field>
        ) : null}

        <Field label={t('goals.category')}>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value as GoalCategory)}
            className={inputCls}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`cat.${c}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('goals.value')}>
          <select
            value={form.valueType}
            onChange={(e) => set('valueType', e.target.value as GoalInput['valueType'])}
            className={inputCls}
          >
            <option value="none">{t('goals.vNone')}</option>
            <option value="fixed">{t('goals.vFixed')}</option>
            <option value="url_param">{t('goals.vUrlParam')}</option>
            <option value="api">{t('goals.vApi')}</option>
          </select>
          {form.valueType === 'fixed' ? (
            <input
              type="number"
              value={form.valueAmount ? form.valueAmount / 100 : ''}
              onChange={(e) => set('valueAmount', Math.round(Number(e.target.value) * 100) || 0)}
              placeholder={t('goals.amount')}
              className={`${inputCls} mt-2`}
            />
          ) : null}
          {form.valueType === 'url_param' ? (
            <input
              value={form.valueParam}
              onChange={(e) => set('valueParam', e.target.value)}
              placeholder={t('goals.paramName')}
              className={`${inputCls} mt-2`}
            />
          ) : null}
        </Field>

        <div className="flex items-center justify-between py-2">
          <span className="text-[12.5px] font-semibold text-text-2">{t('goals.active')}</span>
          <Toggle
            checked={form.active}
            onChange={(v) => set('active', v)}
            aria-label={t('goals.active')}
          />
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <Btn onClick={onClose}>{t('goals.cancel')}</Btn>
          <Btn primary disabled={!form.name || save.isPending} onClick={() => save.mutate()}>
            {t('goals.save')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-[9px] border border-border-strong px-3 py-2 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-[rgba(37,99,235,0.12)]';

function Field({ label, children }: { label: string; children: React.ReactNode }): ReactElement {
  return (
    <div className="mb-3">
      <div className="mb-1 text-[12.5px] font-semibold text-text-2">{label}</div>
      {children}
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
